/**
 * Read surface for every screen.
 *
 * Screens should use these hooks rather than reaching into `snapshot` directly:
 * each one selects the narrowest slice it can, so a photo arriving for player 9
 * does not re-render the ranking board. They are all pure projections of server
 * state — no scoring, no ranking, no phase logic (SPEC §4.2).
 *
 * Agent D: this file plus `actions.ts` is the whole contract. If a screen needs
 * something that isn't here, it can be added here; nothing in `app/` or `net/`
 * needs to change.
 */
import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import {
  MAX_PLAYERS,
  MIN_PLAYERS,
  type Leaderboard,
  type PhotoDataUrl,
  type PlayerId,
  type Prediction,
  type Question,
  type QuestionNumber,
  type RevealPayload,
  type RoomPhase,
  type RoomSettings,
  type RosterEntry,
  type ScoreDelta,
  type RankingHistory,
  type Superlative,
  type TurnState,
} from '@idgame/protocol';
import { useGameStore, type GameStore, type LockState, type RoomError } from './gameStore.js';

/* -------------------------------------------------------------------------- */
/* A player, as screens want them                                              */
/* -------------------------------------------------------------------------- */

/** Roster entry with the photo folded back in, plus a couple of view flags. */
export interface PlayerView extends RosterEntry {
  photo: PhotoDataUrl | null;
  /** This client. */
  isSelf: boolean;
  /** The player taking the current turn. */
  isTurnPlayer: boolean;
}

const EMPTY_PLAYERS: readonly RosterEntry[] = [];
const EMPTY_NUMBERS: readonly QuestionNumber[] = [];
const EMPTY_ORDER: readonly PlayerId[] = [];

function toView(
  entry: RosterEntry,
  photos: Record<PlayerId, PhotoDataUrl | null>,
  selfId: PlayerId | null,
  turnPlayerId: PlayerId | null,
): PlayerView {
  return {
    ...entry,
    photo: photos[entry.id] ?? null,
    isSelf: entry.id === selfId,
    isTurnPlayer: entry.id === turnPlayerId,
  };
}

/* -------------------------------------------------------------------------- */
/* Connection                                                                  */
/* -------------------------------------------------------------------------- */

export function useConnectionStatus(): GameStore['status'] {
  return useGameStore((s) => s.status);
}

/**
 * True only when the drop has lasted long enough to be worth mentioning.
 * Reconnection is meant to be invisible (SPEC §5.4) — the first retry happens
 * inside 300ms and the user should never see anything for that.
 */
export function useIsReconnecting(): boolean {
  return useGameStore((s) => s.status === 'reconnecting' && s.attempt > 1);
}

export function useHasConnected(): boolean {
  return useGameStore((s) => s.everConnected);
}

export function useRoomCode(): string | null {
  return useGameStore((s) => s.roomCode);
}

export function useRoomError(): RoomError | null {
  return useGameStore((s) => s.error);
}

/* -------------------------------------------------------------------------- */
/* Identity and roles                                                          */
/* -------------------------------------------------------------------------- */

export function useSelfId(): PlayerId | null {
  return useGameStore((s) => s.selfId);
}

/** SPEC §5.5 — the host is also just a player; only the buttons differ. */
export function useIsHost(): boolean {
  return useGameStore((s) => s.isHost);
}

export function useIsTurnPlayer(): boolean {
  return useGameStore((s) => s.selfId !== null && s.snapshot?.turn?.turnPlayerId === s.selfId);
}

export function useSelf(): PlayerView | null {
  return usePlayer(useSelfId());
}

/* -------------------------------------------------------------------------- */
/* Phase and turn                                                              */
/* -------------------------------------------------------------------------- */

export function usePhase(): RoomPhase | null {
  return useGameStore((s) => s.snapshot?.phase ?? null);
}

export function useTurn(): TurnState | null {
  return useGameStore((s) => s.snapshot?.turn ?? null);
}

export function useTurnPlayerId(): PlayerId | null {
  return useGameStore((s) => s.snapshot?.turn?.turnPlayerId ?? null);
}

export function useTurnPlayer(): PlayerView | null {
  return usePlayer(useTurnPlayerId());
}

/** The current question, once revealed. Null during question-select. */
export function useQuestion(): Question | null {
  return useGameStore((s) => s.snapshot?.turn?.question ?? null);
}

/** Greyed out on the numbered grid (SPEC §2.3 phase 1). Survives "play again". */
export function useUsedQuestionNumbers(): readonly QuestionNumber[] {
  return useGameStore((s) => s.snapshot?.usedQuestionNumbers ?? EMPTY_NUMBERS);
}

/** "turn 5 of 24" — 1-based position, and the total. */
export function useTurnProgress(): { current: number; total: number; round: number } {
  return useGameStore(
    useShallow((s) => ({
      current: (s.snapshot?.turn?.index ?? 0) + 1,
      total: s.snapshot?.turnsTotal ?? 0,
      round: s.snapshot?.turn?.round ?? 1,
    })),
  );
}

/* -------------------------------------------------------------------------- */
/* Roster                                                                      */
/* -------------------------------------------------------------------------- */

/** Every seat, in roster order, photos attached. Removed players included. */
export function usePlayers(): PlayerView[] {
  const players = useGameStore((s) => s.snapshot?.players ?? EMPTY_PLAYERS);
  const photos = useGameStore((s) => s.photos);
  const selfId = useGameStore((s) => s.selfId);
  const turnPlayerId = useGameStore((s) => s.snapshot?.turn?.turnPlayerId ?? null);

  return useMemo(
    () => players.map((entry) => toView(entry, photos, selfId, turnPlayerId)),
    [players, photos, selfId, turnPlayerId],
  );
}

/**
 * The card set for ranking and prediction. A player who leaves **stays on the
 * roster and stays rankable for the entire game** (SPEC §5.4); only an explicit
 * host removal takes them out.
 */
export function useRankablePlayers(): PlayerView[] {
  const players = usePlayers();
  return useMemo(() => players.filter((player) => !player.removed), [players]);
}

export function usePlayer(playerId: PlayerId | null | undefined): PlayerView | null {
  const players = usePlayers();
  return useMemo(
    () => (playerId ? (players.find((player) => player.id === playerId) ?? null) : null),
    [players, playerId],
  );
}

/** Names by id, for attribution lines ("Sam, Priya, Alex, Jo"). */
export function usePlayerNames(): Record<PlayerId, string> {
  const players = usePlayers();
  return useMemo(() => {
    const out: Record<PlayerId, string> = {};
    for (const player of players) out[player.id] = player.name;
    return out;
  }, [players]);
}

/** Formats a list of ids as names: "Sam, Priya and Alex". */
export function useNameList(): (ids: readonly PlayerId[]) => string {
  const names = usePlayerNames();
  return useMemo(
    () => (ids) => formatNameList(ids.map((id) => names[id] ?? 'Someone')),
    [names],
  );
}

export function formatNameList(names: readonly string[]): string {
  if (names.length === 0) return '';
  if (names.length === 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** Fixed at game start (SPEC §2.1), shown in the lobby. Empty before then. */
export function useTurnOrder(): PlayerView[] {
  const order = useGameStore((s) => s.snapshot?.turnOrder ?? EMPTY_ORDER);
  const players = usePlayers();
  return useMemo(() => {
    const byId = new Map(players.map((player) => [player.id, player]));
    return order.flatMap((id) => {
      const player = byId.get(id);
      return player ? [player] : [];
    });
  }, [order, players]);
}

export function usePlayerCount(): number {
  return useGameStore((s) => s.snapshot?.players.filter((p) => !p.removed).length ?? 0);
}

/** SPEC §2.1 — below 4, top/bottom prediction is a coin flip. */
export function useCanStart(): boolean {
  const count = usePlayerCount();
  const everyoneReady = useGameStore((s) =>
    (s.snapshot?.players ?? EMPTY_PLAYERS).every((p) => p.removed || p.hasPhoto),
  );
  return count >= MIN_PLAYERS && count <= MAX_PLAYERS && everyoneReady;
}

/* -------------------------------------------------------------------------- */
/* Settings and lobby                                                          */
/* -------------------------------------------------------------------------- */

export function useSettings(): RoomSettings | null {
  return useGameStore((s) => s.snapshot?.settings ?? null);
}

/** ms, recomputed as players join (SPEC §2.2). */
export function useEstimatedDurationMs(): number {
  return useGameStore((s) => s.estimatedDurationMs);
}

/**
 * The server's ceiling on rounds: `floor(unburned numbers / players)`, clamped
 * to the protocol's 1–10.
 *
 * **Render this; never recompute it.** Only the server knows how much of the
 * numbered grid is still unburned, which is why a client-side
 * `floor(100 / players)` agreed on a fresh game and diverged after a play-again.
 */
export function useMaxRounds(): number {
  return useGameStore((s) => s.maxRounds);
}

/** SPEC §2.6 — "play again" had to wipe the grid. Shown once, in the lobby. */
export function useDeckReshuffled(): boolean {
  return useGameStore((s) => s.deckReshuffled);
}

/* -------------------------------------------------------------------------- */
/* Rank & predict                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Who the room is still waiting on, as people (SPEC §2.3 phase 2).
 *
 * There is no `usePredictionCount` any more. The turn player's *"7 of 11
 * predictions in"* is gone — the count never informed a decision and only
 * tempted them to hold a finished ranking hostage to somebody else's two taps —
 * and the server no longer sends them the frame at all. What is left is this,
 * and it is for predictors: connected seats that have not locked in, drawn as
 * faces on the room board.
 *
 * **Never what anyone picked.** These are player ids and nothing else; the
 * content of a prediction reaches no phone before the reveal.
 *
 * Empty for the turn player by construction — the worker sends them an empty
 * list — so no screen has to remember to hide it.
 *
 * **Self drops off the moment this seat locks, not when the server says so.**
 * `lock_prediction` goes out and the room board appears in the same frame, so
 * for one round trip the server's list still names this player — and the board
 * would open by counting its owner among the people it is waiting for, one over
 * on the very screen they just earned. Worse, a player who locks *last* would
 * never see the collapsed "Maya is still ranking" board at all, because that
 * needs an empty list. The window is one RTT on a good network and unbounded
 * while the socket is queued (SPEC §5.4 — the amber reconnect bar is exactly
 * when this matters), so the answer is not to wait for the frame: the lock is a
 * ratchet within the turn, so subtracting self is safe even if a
 * `prediction_count` built before the lock lands afterwards.
 */
export function usePendingPredictors(): PlayerView[] {
  const pending = useGameStore((s) => s.snapshot?.pendingPredictors ?? EMPTY_ORDER);
  const locked = useGameStore((s) => s.selfPredictionLocked);
  const selfId = useSelfId();
  const players = usePlayers();
  return useMemo(() => {
    const byId = new Map(players.map((player) => [player.id, player]));
    return pending.flatMap((id) => {
      if (locked && id === selfId) return [];
      const player = byId.get(id);
      return player ? [player] : [];
    });
  }, [pending, players, locked, selfId]);
}

/** This client's own picks as the server holds them, and whether they can change. */
export function useSelfPrediction(): { prediction: Prediction | null; editable: boolean } {
  return useGameStore(
    useShallow((s) => ({
      prediction: s.selfPrediction,
      editable: s.selfPredictionEditable,
    })),
  );
}

/**
 * This seat has pressed Submit and there is no way back (SPEC §2.3 phase 3).
 *
 * Separate from `useSelfPrediction().editable`, which the room's hard lock also
 * turns off: locking is what swaps the face grid for the room board, and zero
 * arriving on a player who never locked must not do that.
 */
export function useSelfPredictionLocked(): boolean {
  return useGameStore((s) => s.selfPredictionLocked);
}

/* -------------------------------------------------------------------------- */
/* The lock (SPEC §2.3 phase 3)                                                */
/* -------------------------------------------------------------------------- */

/**
 * The guillotine, already translated onto the **local** clock so a countdown can
 * be rendered with plain `Date.now()`. Null when no lock is running.
 */
export function useLock(): (LockState & { localEndsAt: number }) | null {
  const lock = useGameStore((s) => s.lock);
  const offset = useGameStore((s) => s.serverOffsetMs);
  return useMemo(
    () => (lock ? { ...lock, localEndsAt: lock.endsAt - offset } : null),
    [lock, offset],
  );
}

/** `serverEpochMs -> localEpochMs`. Use for any server-scheduled animation. */
export function useServerClock(): { offsetMs: number; toLocal: (serverMs: number) => number } {
  const offsetMs = useGameStore((s) => s.serverOffsetMs);
  return useMemo(
    () => ({ offsetMs, toLocal: (serverMs: number) => serverMs - offsetMs }),
    [offsetMs],
  );
}

/* -------------------------------------------------------------------------- */
/* Reveal, justification, leaderboard, superlatives                            */
/* -------------------------------------------------------------------------- */

export function useReveal(): RevealPayload | null {
  return useGameStore((s) => s.reveal);
}

/** This client's own score delta for the turn just revealed (SPEC §2.3 §4). */
export function useSelfDelta(): ScoreDelta | null {
  const reveal = useReveal();
  const selfId = useSelfId();
  return useMemo(
    () => (reveal && selfId ? (reveal.deltas.find((d) => d.playerId === selfId) ?? null) : null),
    [reveal, selfId],
  );
}

export function useJustification(): GameStore['justification'] {
  return useGameStore((s) => s.justification);
}

export function useLeaderboard(): Leaderboard | null {
  return useGameStore((s) => s.leaderboard);
}

export function useNextRound(): number | null {
  return useGameStore((s) => s.nextRound);
}

export function useSuperlatives(): Superlative[] | null {
  return useGameStore((s) => s.superlatives);
}

/**
 * Every completed ranking of the game, oldest first (SPEC §2.6). Empty until
 * the `superlatives` frame lands — and empty forever in a game where every turn
 * was voided, which produces no rankings.
 */
export function useRankingHistory(): RankingHistory {
  return useGameStore((s) => s.rankingHistory);
}

export function useGameEndedReason(): GameStore['gameEndedReason'] {
  return useGameStore((s) => s.gameEndedReason);
}

/* -------------------------------------------------------------------------- */
/* Host controls (SPEC §5.5)                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Set when the turn player has been gone longer than the 30s grace period and
 * the host is being offered *"Skip [name]'s turn."* (SPEC §5.4). Null otherwise,
 * and always null for non-hosts.
 */
export function useSkipOffer(): PlayerView | null {
  const skipOfferId = useGameStore((s) => s.skipOffer);
  return usePlayer(skipOfferId);
}

/* -------------------------------------------------------------------------- */
/* The seat picker (SPEC §5.4)                                                 */
/* -------------------------------------------------------------------------- */

/** Just enough of a player to draw a `LicenseCard` for a seat nobody is in. */
export interface ClaimableSeat {
  id: PlayerId;
  name: string;
  photo: PhotoDataUrl | null;
}

/**
 * Empty seats this device has been offered, already carrying whatever photo
 * the server pushed for them, or null when it was not offered any.
 *
 * Deliberately not `PlayerView`. This device has no seat and therefore no
 * roster: the offer is everything it has been told about the room, and a
 * half-invented roster entry — `connected: false`, `joinedAt: 0`, a turn order
 * it cannot know — would be a lie the rest of the app could read.
 */
export function useClaimableSeats(): { seats: ClaimableSeat[]; canJoinAsNew: boolean } | null {
  const offered = useGameStore((s) => s.claimableSeats);
  const photos = useGameStore((s) => s.photos);
  const canJoinAsNew = useGameStore((s) => s.canJoinAsNew);

  return useMemo(
    () =>
      offered
        ? {
            seats: offered.map((seat) => ({
              id: seat.playerId,
              name: seat.name,
              photo: photos[seat.playerId] ?? null,
            })),
            canJoinAsNew,
          }
        : null,
    [offered, photos, canJoinAsNew],
  );
}

/* -------------------------------------------------------------------------- */
/* Audio                                                                       */
/* -------------------------------------------------------------------------- */

export function useMuted(): boolean {
  return useGameStore((s) => s.muted);
}

/* -------------------------------------------------------------------------- */
/* Onboarding draft (join flow only)                                           */
/* -------------------------------------------------------------------------- */

export function useDraft(): GameStore['draft'] {
  return useGameStore((s) => s.draft);
}
